import React, { useState, useRef, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CheckIcon, CircleDashed } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useIssuesStore } from "@/store/issues-store";
import { toast } from "sonner";
import { Status } from "@/types/status";
import { cn } from "@/lib/utils";

interface StatusBadgeProps {
  status?: Status | null;
  className?: string;
  issueId: string;
  statuses?: Status[];
  onChange?: (status: Status) => void;
  variant?: "outline" | "default" | "text-dark";
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  className = "",
  issueId,
  statuses = [],
  onChange,
  variant = "default",
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { updateIssueStatus } = useIssuesStore();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchQuery("");
    }
  }, [isOpen]);

  const filteredStatuses = statuses.filter((s) =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStatusChange = async (newStatus: Status) => {
    if (onChange) {
      onChange(newStatus);
      return;
    }

    if (!issueId) {
      toast.error("Could not update status");
      return;
    }

    try {
      await updateIssueStatus(issueId, newStatus);
      toast.success(`Status updated to ${newStatus.name}`);
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    }
  };

  const filteredStatus = filteredStatuses.find((s) => s.name === status?.name);

  const BadgeIcon = filteredStatus?.badgeIcon ?? filteredStatus?.icon;
  const StatusIcon = filteredStatus?.icon;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <div>
          {variant === "text-dark" ? (
            <div className="flex items-center justify-center h-7 w-fit p-0 bg-secondary-0 rounded-md hover:bg-secondary-n-800 border border-border-primary cursor-pointer">
              {StatusIcon ? (
                <div className="flex items-center gap-1 p-1.5">
                  <StatusIcon />
                  <span className="text-xs font-medium text-text-primary">
                    {status?.name}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1 p-1.5">
                  <CircleDashed className="size-4 pl-[3px]" color="#F5F5F6" />
                  <span className="text-xs font-medium text-text-primary">
                    Status
                  </span>
                </div>
              )}
            </div>
          ) : (
            <Badge
              className={cn(
                "h-6 w-6 p-0 flex items-center justify-center",
                "text-muted-foreground hover:bg-secondary-n-800",
                className
              )}
            >
              {BadgeIcon && <BadgeIcon />}
            </Badge>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={menuRef}
        className="w-[200px] overflow-hidden bg-secondary-0 rounded-lg"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
        onPointerDownOutside={(e) => {
          if (!menuRef.current?.contains(e.target as Node)) {
            setIsOpen(false);
          }
        }}
        onInteractOutside={(e) => {
          if (searchInputRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-2 py-1.5">
          <Input
            ref={searchInputRef}
            placeholder="Change status"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="h-7 text-sm bg-secondary-n-950 border-0"
          />
        </div>
        <DropdownMenuSeparator />
        <div
          className="overflow-y-auto overflow-x-hidden context-menu-scroll"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
          }}
        >
          <div className="pl-4 py-1 caption text-2xs font-medium text-secondary-n-400">
            Statuses
          </div>
          {filteredStatuses.map((s) => {
            const Icon = s.icon;
            return (
              <DropdownMenuItem
                key={s.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStatusChange(s);
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center justify-between w-full py-1 pl-2">
                  <div className="flex items-center gap-2">
                    <Icon />
                    <span>{s.name}</span>
                  </div>
                  {status?.name === s.name && (
                    <CheckIcon className="size-3.5" color="#F5F5F6" />
                  )}
                </div>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
